import i18n from 'ui/attribute-label/attribute-formatting-utils.properties';
import { createResourceLabel } from 'ui/resource-label/resource-label';
import { createResourceSummary } from 'ui/resource-summary/resource-summary';
import { type DataSourceLabelOptions, createDataSourceLabel } from 'ui/resource-summary/resource-summary-utils';

import { createPublicationLabel } from 'cadenza/admin/publish-objecttype-dialog/publish-objecttype-actions';
import type { ObjecttypeDto } from 'cadenza/api-client/dto/objecttype-dto';
import { DataSourceId } from 'cadenza/repository/model/data-source-id.gen';
import { getTableResourceType } from 'cadenza/repository/model/data-source-type-util';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import {
  createObjecttypeDetailsUrl,
  dataSourceAnchorWithResourceLabel,
  projectAnchorWithResourceLabel,
  repositoryAnchorWithResourceLabel,
} from 'cadenza/utils/admin-url-utils';
import type { Objecttype } from 'cadenza/workbook/api/objecttype';

interface ObjecttypeMetaLabelOptions extends DataSourceLabelOptions {
  repositoryPrintName: string;
  tableName?: string;
}

type ObjecttypeSummaryObjecttype = Pick<
  Objecttype | ObjecttypeDto,
  | 'id'
  | 'printName'
  | 'isCombinedObjecttype'
  | 'dataSourceName'
  | 'projectId'
  | 'projectPrintName'
  | 'projectKind'
  | 'publicationState'
> &
  ObjecttypeMetaLabelOptions & {
    withManagementLink?: boolean;
    withPublicationState?: boolean;
    repositoryName?: RepositoryName;
  };

export function createObjecttypeSummary(objecttype: ObjecttypeSummaryObjecttype, canSeeDatasource?: boolean) {
  const metaLabels: HTMLElement[] = [
    (
      objecttype.repositoryName // Initially, we only have the repository print name, so we cannot link the resource label to the repository
    ) ?
      repositoryAnchorWithResourceLabel(objecttype.repositoryName, objecttype.repositoryPrintName)
    : createResourceLabel('repository', objecttype.repositoryPrintName),
  ];

  if (objecttype.projectId) {
    metaLabels.push(
      projectAnchorWithResourceLabel({
        itemId: objecttype.projectId,
        projectKind: objecttype.projectKind!,
        printName: objecttype.projectPrintName!,
      }),
    );
  } else {
    metaLabels.push(
      canSeeDatasource && objecttype.repositoryName ?
        dataSourceAnchorWithResourceLabel(
          DataSourceId.of(objecttype.repositoryName, objecttype.dataSourceName),
          objecttype.dataSourcePrintName,
        )
      : createDataSourceLabel(objecttype),
    );
  }

  if (objecttype.tableName) {
    metaLabels.push(createResourceLabel(getTableResourceType(objecttype.dataSourceType), objecttype.tableName));
  }

  if (objecttype.withPublicationState) {
    metaLabels.push(createPublicationLabel(objecttype.publicationState));
  }

  const header = createResourceLabel(
    objecttype.isCombinedObjecttype ? 'combinedObjecttype' : 'objecttype',
    objecttype.printName,
  );
  const headlineOptions =
    objecttype.withManagementLink ?
      {
        managementCenterLink: createObjecttypeDetailsUrl(objecttype.id),
        managementCenterLinkLabel: i18n('openInManagementCenter.objecttype'),
      }
    : undefined;
  return createResourceSummary(header, metaLabels, headlineOptions);
}
